import React, { useState, useCallback, memo, useRef, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { T } from '../T';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  MagnifyingGlassIcon,
  HeartIcon,
  ArrowRightIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolid } from '@heroicons/react/24/solid';
import { fetchAllEvents } from '../../services/eventsApi';
import { dtLocale } from '../../utils/dateLocale';

// Category colors and icons
const CATEGORY_CONFIG = {
  'Wellness & Fitness': { icon: '💪', color: '#10b981' },
  'Music & Nightlife': { icon: '🎵', color: '#8b5cf6' },
  'Food & Social': { icon: '🍽️', color: '#f59e0b' },
  'Sports & Recreation': { icon: '⚽', color: '#3b82f6' },
  'Arts & Culture': { icon: '🎨', color: '#ec4899' },
  'Education & Workshops': { icon: '🛠️', color: '#06b6d4' },
  'Networking & Business': { icon: '🤝', color: '#6366f1' },
  'Entertainment': { icon: '🎭', color: '#ef4444' },
  'Family & Kids': { icon: '👨‍👩‍👧', color: '#f97316' },
  'Technology': { icon: '💻', color: '#14b8a6' },
  'Promos': { icon: '🎁', color: '#ef4444' }
};

// Event subtypes mapping
const EVENT_SUBTYPES = {
  'Promos': [
    { value: 'Happy Hour', label: 'Happy Hour', icon: '🍻' }
  ],
  'Music & Nightlife': [
    { value: 'Live DJ Set', label: 'Live DJ Set', icon: '🎧' },
    { value: 'Live Band', label: 'Live Band', icon: '🎸' }
  ],
  'Entertainment': [
    { value: 'Trivia Night', label: 'Trivia Night', icon: '🧠' },
    { value: 'Karaoke Night', label: 'Karaoke Night', icon: '🎤' }
  ],
  'Food & Social': [
    { value: 'Beach Club Brunch', label: 'Beach Club Brunch', icon: '🏖️' }
  ],
  'Sports & Recreation': [
    { value: 'Live Sports Screening', label: 'Live Sports Screening', icon: '📺' },
    { value: 'Fitness Class', label: 'Fitness Class', icon: '💪' },
    { value: 'Beach Volleyball', label: 'Beach Volleyball', icon: '🏐' }
  ],
  'Wellness & Fitness': [
    { value: 'Pilates Class', label: 'Pilates Class', icon: '🧘' },
    { value: 'Yoga Session', label: 'Yoga Session', icon: '🧘‍♀️' },
    { value: 'CrossFit WOD', label: 'CrossFit WOD', icon: '🏋️' }
  ],
  'Arts & Culture': [
    { value: 'Open Mic', label: 'Open Mic', icon: '🎙️' },
    { value: 'Comedy Night', label: 'Comedy Night', icon: '😂' }
  ],
  'Education & Workshops': [
    { value: 'Workshop', label: 'Workshop', icon: '🛠️' },
    { value: 'Mixology Class', label: 'Mixology Class', icon: '🍸' }
  ]
};

// Optimized Image Component
const OptimizedImage = memo(({ src, alt, style }) => {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);

  return (
    <>
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        onLoad={() => setLoaded(true)}
        style={{
          ...style,
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.3s'
        }}
      />
      {!loaded && (
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: '#f3f4f6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e5e7eb',
            borderTop: '3px solid #ea580c',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
        </div>
      )}
    </>
  );
});

OptimizedImage.displayName = 'OptimizedImage';

// Date Range Picker Component
const DateRangePicker = memo(({ startDate, endDate, onStartDateChange, onEndDateChange, onClear }) => {
  const { t } = useTranslation();
  const [showPicker, setShowPicker] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);

  const formatDisplayDate = useCallback((date) => {
    if (!date) return t('events.selectDate');
    const d = new Date(date);
    return d.toLocaleDateString(dtLocale(), { month: 'short', day: 'numeric', year: 'numeric' });
  }, []);

  const handleApply = useCallback(() => {
    onStartDateChange(tempStartDate);
    onEndDateChange(tempEndDate);
    setShowPicker(false);
  }, [tempStartDate, tempEndDate, onStartDateChange, onEndDateChange]);

  const handleClear = useCallback(() => {
    setTempStartDate('');
    setTempEndDate('');
    onStartDateChange('');
    onEndDateChange('');
    setShowPicker(false);
    if (onClear) onClear();
  }, [onStartDateChange, onEndDateChange, onClear]);

  const today = useMemo(() => new Date().toISOString().split('T')[0], []);

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <button
        onClick={() => setShowPicker(!showPicker)}
        style={{
          width: '100%',
          padding: '10px 12px',
          borderRadius: '10px',
          border: '1px solid #e5e7eb',
          backgroundColor: 'white',
          fontSize: '14px',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          transition: 'all 0.2s',
          boxSizing: 'border-box'
        }}
      >
        <CalendarIcon style={{ height: '18px', width: '18px', color: '#9ca3af', flexShrink: 0 }} />
        <span style={{ 
          flex: 1, 
          color: startDate || endDate ? '#111827' : '#9ca3af',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {startDate || endDate
            ? `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`
            : t('events.selectDateRange')}
        </span>
        {(startDate || endDate) && (
          <XMarkIcon 
            style={{ height: '16px', width: '16px', color: '#6b7280', flexShrink: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
          />
        )}
      </button>

      <AnimatePresence>
        {showPicker && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              left: 0,
              right: 0,
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 12px 24px rgba(0, 0, 0, 0.15)',
              padding: '20px',
              zIndex: 1000,
              border: '1px solid #e5e7eb'
            }}
          >
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px'
              }}>
                {t('events.dateFrom')}
              </label>
              <input
                type="date"
                value={tempStartDate}
                min={today}
                onChange={(e) => setTempStartDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px'
              }}>
                {t('events.dateTo')}
              </label>
              <input
                type="date"
                value={tempEndDate}
                min={tempStartDate || today}
                onChange={(e) => setTempEndDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleClear}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {t('events.dateClear')}
              </button>
              <button
                onClick={handleApply}
                disabled={!tempStartDate && !tempEndDate}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: tempStartDate || tempEndDate ? '#ea580c' : '#e5e7eb',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: tempStartDate || tempEndDate ? 'pointer' : 'not-allowed'
                }}
              >
                {t('events.dateApply')}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

DateRangePicker.displayName = 'DateRangePicker';

// Event Card Component
const AllEventCard = memo(({ event, onLike, isLiked, onClick, t }) => {
  const categoryConfig = useMemo(() => 
    CATEGORY_CONFIG[event.category] || { icon: '🎪', color: '#ea580c' },
    [event.category]
  );

  const handleLikeClick = useCallback((e) => {
    e.stopPropagation();
    onLike(event.id);
  }, [event.id, onLike]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={onClick}
      style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s'
      }}
      whileHover={{ transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)' }}
    >
      <div style={{
        position: 'relative',
        height: '200px',
        overflow: 'hidden',
        backgroundColor: '#f3f4f6'
      }}>
        <OptimizedImage
          src={event.image}
          alt={event.title}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />

        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.7) 100%)'
        }} />

        {event.isFree && (
          <div style={{
            position: 'absolute',
            top: '12px',
            left: '12px',
            padding: '6px 10px',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: '600',
            backgroundColor: '#10b981',
            color: 'white'
          }}>
            FREE
          </div>
        )}

        {event.isPast && !event.isToday && (
          <div style={{
            position: 'absolute',
            top: '12px',
            left: event.isFree ? '70px' : '12px',
            padding: '6px 10px',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: '600',
            backgroundColor: '#6b7280',
            color: 'white'
          }}>
            PAST
          </div>
        )}

        {event.isToday && (
          <div style={{
            position: 'absolute',
            top: '12px',
            left: event.isFree ? '70px' : '12px',
            padding: '6px 10px',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: '600',
            backgroundColor: '#ef4444',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <div style={{
              width: '6px',
              height: '6px',
              backgroundColor: 'white',
              borderRadius: '50%',
              animation: 'pulse 2s infinite'
            }} />
            LIVE TODAY
          </div>
        )}

        <button
          onClick={handleLikeClick}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(8px)'
          }}
        >
          {isLiked ? (
            <HeartSolid style={{ height: '18px', width: '18px', color: '#ef4444' }} />
          ) : (
            <HeartIcon style={{ height: '18px', width: '18px', color: '#6b7280' }} />
          )}
        </button>

        <div style={{
          position: 'absolute',
          bottom: '12px',
          left: '12px',
          right: '12px'
        }}>
          <h3 style={{
            color: 'white',
            fontSize: '18px',
            fontWeight: '700',
            margin: '0 0 4px 0',
            textShadow: '0 2px 4px rgba(0,0,0,0.5)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {event.title}
          </h3>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            color: 'rgba(255,255,255,0.95)',
            fontSize: '13px',
            textShadow: '0 1px 2px rgba(0,0,0,0.5)'
          }}>
            <MapPinIcon style={{ height: '14px', width: '14px' }} />
            <span style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {event.venue}
            </span>
          </div>
        </div>
      </div>

      <div style={{ padding: '16px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <CalendarIcon style={{ height: '16px', width: '16px', color: '#6b7280' }} />
            <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>
              {event.date}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <ClockIcon style={{ height: '16px', width: '16px', color: '#6b7280' }} />
            <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>
              {event.time}
            </span>
          </div>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '12px'
        }}>
          <span style={{
            fontSize: '12px',
            padding: '4px 8px',
            backgroundColor: `${categoryConfig.color}15`,
            color: categoryConfig.color,
            borderRadius: '6px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <span>{categoryConfig.icon}</span>
            {event.category}
          </span>

          <span style={{
            fontSize: '16px',
            fontWeight: '700',
            color: event.isFree ? '#10b981' : '#ea580c'
          }}>
            {event.price}
          </span>
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          style={{
            width: '100%',
            padding: '10px',
            background: 'linear-gradient(135deg, #ea580c, #f59e0b)',
            color: 'white',
            borderRadius: '10px',
            fontWeight: '600',
            fontSize: '14px',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          {t ? t('events.viewVenue') : 'View Venue'}
          <ArrowRightIcon style={{ height: '16px', width: '16px' }} />
        </motion.button>
      </div>
    </motion.div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.event.id === nextProps.event.id &&
    prevProps.isLiked === nextProps.isLiked
  );
});

AllEventCard.displayName = 'AllEventCard';

const AllEventsPage = ({ onClose, onFilterChange }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubtype, setSelectedSubtype] = useState('');
  const [likedEvents, setLikedEvents] = useState(new Set());
  
  const observerTarget = useRef(null);

  // Helper functions
  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return t('vs.today');
    if (date.toDateString() === tomorrow.toDateString()) return t('events.tomorrow');

    return date.toLocaleDateString(dtLocale(), {
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    });
  }, []);

  const formatTime = useCallback((startTime) => {
    const start = new Date(startTime);
    return start.toLocaleTimeString(dtLocale(), { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    });
  }, []);

  const formatPrice = useCallback((priceFrom, priceMax, currency) => {
    if (priceFrom === 0 && priceMax === 0) return t('vs.priceFree');
    if (priceFrom === priceMax) return `${currency} ${priceFrom}`;
    return `${currency} ${priceFrom}-${priceMax}`;
  }, []);

  const isToday = useCallback((dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }, []);

  const getEventImage = useCallback((eventType) => {
    const imageMap = {
      'et_yoga-session': 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&q=80',
      'et_pilates-class': 'https://images.unsplash.com/photo-1518611012118-696072aa579a?w=400&q=80',
      'et_crossfit-wod': 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400&q=80',
      'et_live-band': 'https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=400&q=80',
      'et_happy-hour': 'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=400&q=80',
      'et_dj-night': 'https://images.unsplash.com/photo-1571266028243-d220bc80b1ca?w=400&q=80',
      'et_trivia': 'https://images.unsplash.com/photo-1533158326339-7f3cf2404354?w=400&q=80',
      'et_comedy': 'https://images.unsplash.com/photo-1585699324551-f6c309eedeca?w=400&q=80',
      'et_workshop': 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=400&q=80',
      'et_beach-club-brunch': 'https://images.unsplash.com/photo-1559329007-40df8a9345d8?w=400&q=80'
    };
    return imageMap[eventType] || 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=400&q=80';
  }, []);

  // State for showing past events
  const [showPastEvents, setShowPastEvents] = useState(true); // Default to true to show test data

  // React Query infinite query with client-side filtering
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError
  } = useInfiniteQuery({
    queryKey: ['all-events', showPastEvents],
    queryFn: ({ pageParam = 1 }) => {
      const params = {
        page: pageParam,
        limit: 200,
        upcoming: !showPastEvents // If showing past events, don't filter by upcoming
      };
      
      console.log('🔍 Fetching all events:', params);
      return fetchAllEvents(params);
    },
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.pagination && pages.length < lastPage.pagination.totalPages) {
        return pages.length + 1;
      }
      return undefined;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  // Transform and filter events - FIXED: Support both old Dubai schema and new TH schema
  const events = useMemo(() => {
    if (!data) return [];
    
    const transformedEvents = data.pages.flatMap(page => 
      page.data.map(event => {
        // Support both camelCase (new TH schema) and snake_case (old Dubai schema)
        const eventName = event.eventName || event.Event_Name || event.EventName || t('events.unnamedEvent');
        const startDate = event.eventStartsAt || event.EventStartsAt || event.EventStarts_At;
        const endDate = event.eventEndsAt || event.EventEndsAt || event.EventEnds_At;
        const priceFrom = event.eventPriceFrom ?? event.EventPricefrom ?? event.Event_Price_from ?? 0;
        const priceMax = event.eventPriceMax ?? event.EventPriceMax ?? event.Event_Price_Max ?? 0;
        const currency = event.eventCurrency || event.EventCurrency || event.Event_Currency || 'THB';
        const category = event.eventCategory || event.EventCategory || event.Event_Category || 'Entertainment';
        const eventType = event.eventType || event.Eventtype || event.Event_type || '';
        const description = event.description || event.Evendescription || event.Even_description || '';
        const ageRestriction = event.eventAgeRestriction || event.EventAgeRestriction || event.Event_Age_Restriction || '';
        const venueName = event.venueId?.name || t('review.unknownVenue');
        const venueId = event.venueId?._id || event.venueId || event.Dubai_id || '';
        const images = event.images || [];
        const coverPhoto = event.coverPhotoUrl || event.eventPhotoUrl || event.imageUrl;
        
        return {
          id: event._id || event.Dubai_event_id || event.id,
          title: eventName,
          venue: venueName,
          venueId: venueId,
          date: formatDate(startDate),
          time: formatTime(startDate),
          price: formatPrice(priceFrom, priceMax, currency),
          // Use uploaded images first, then fallback to stock images
          image: images[0] || coverPhoto || getEventImage(eventType),
          images: images,
          category: category,
          description: description,
          isFree: priceFrom === 0 && priceMax === 0,
          isToday: isToday(startDate),
          isPast: new Date(startDate) < new Date(),
          ageRestriction: ageRestriction,
          eventType: event.Eventtypsdisplayname || event.Event_typs_displayname || eventType,
          // Additional fields for display
          priceFrom,
          priceMax,
          currency,
          startDate,
          endDate
        };
      })
    );
    
    console.log('🎯 Total events before filtering:', transformedEvents.length);
    
    // Client-side filtering
    let filteredEvents = transformedEvents;
    
    if (selectedCategory) {
      filteredEvents = filteredEvents.filter(event => 
        event.category === selectedCategory
      );
      console.log('🎯 After category filter:', filteredEvents.length);
    }

    if (selectedSubtype) {
      filteredEvents = filteredEvents.filter(event => 
        event.eventType === selectedSubtype
      );
      console.log('🎯 After subtype filter:', filteredEvents.length);
    }
    
    if (searchQuery && searchQuery.trim()) {
      const searchTerm = searchQuery.trim().toLowerCase();
      filteredEvents = filteredEvents.filter(event => 
        event.title.toLowerCase().includes(searchTerm) ||
        event.venue.toLowerCase().includes(searchTerm) ||
        event.description.toLowerCase().includes(searchTerm) ||
        event.category.toLowerCase().includes(searchTerm)
      );
      console.log('🎯 After search filter:', filteredEvents.length);
    }
    
    console.log('🎯 Final filtered events:', filteredEvents.length);
    
    return filteredEvents;
  }, [data, formatDate, formatTime, formatPrice, isToday, getEventImage, searchQuery, selectedCategory, selectedSubtype]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleLikeEvent = useCallback((eventId) => {
    setLikedEvents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  }, []);

  const handleEventClick = useCallback((event) => {
    const venueId = event.venueId;
    if (venueId) {
      navigate(`/venue/${venueId}`);
    }
  }, [navigate]);

  const handleClearAll = useCallback(() => {
    setSearchQuery('');
    setStartDate('');
    setEndDate('');
    setSelectedCategory('');
    setSelectedSubtype('');
  }, []);

  const handleCategorySelect = useCallback((category) => {
    setSelectedCategory(category);
    setSelectedSubtype('');
  }, []);

  const handleSubtypeSelect = useCallback((subtype) => {
    setSelectedSubtype(subtype);
  }, []);

  const hasActiveFilters = useMemo(() => 
    searchQuery || startDate || endDate || selectedCategory || selectedSubtype,
    [searchQuery, startDate, endDate, selectedCategory, selectedSubtype]
  );

  // Notify parent component of filter changes
  useEffect(() => {
    if (onFilterChange) {
      const filters = {
        searchQuery,
        selectedCategory,
        selectedSubtype,
        startDate,
        endDate
      };
      console.log('🎯 Sending filters to parent:', filters);
      onFilterChange(filters);
    }
  }, [searchQuery, selectedCategory, selectedSubtype, startDate, endDate, onFilterChange]);

  return (
    <>
      <style>
        {`
          .category-carousel::-webkit-scrollbar,
          .subtype-carousel::-webkit-scrollbar {
            display: none;
          }
          .category-carousel,
          .subtype-carousel {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={{
        height: '100%',
        overflow: 'auto',
        backgroundColor: '#f9fafb'
      }}>
        {/* Header */}
        <div style={{
          top: 0,
          zIndex: 10,
          backgroundColor: '#f9fafb',
          padding: '16px 16px 0 16px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px'
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{
                fontSize: 'clamp(24px, 5vw, 32px)',
                fontWeight: '800',
                color: '#111827',
                margin: '0 0 4px 0',
                lineHeight: 1.2
              }}>
                <T k="events.allEvents" />
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <p style={{
                  fontSize: 'clamp(14px, 3vw, 16px)',
                  color: '#6b7280',
                  margin: 0,
                  lineHeight: 1.4
                }}>
                  <T k="events.eventsFound" values={{ count: events.length }} />
                </p>
                {/* Toggle for showing past events */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowPastEvents(!showPastEvents)}
                  style={{
                    padding: '4px 10px',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    backgroundColor: showPastEvents ? '#fef3c7' : 'white',
                    color: showPastEvents ? '#92400e' : '#6b7280',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  {showPastEvents ? `📅 ${t('events.allEventsToggle')}` : `🔮 ${t('events.upcomingOnly')}`}
                </motion.button>
              </div>
            </div>
            
            {onClose && (
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={onClose}
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  marginLeft: '12px'
                }}
              >
                <XMarkIcon style={{ height: '20px', width: '20px', color: '#6b7280' }} />
              </motion.button>
            )}
          </div>

          {/* Search and Filter Bar */}
          <div style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '16px',
            flexWrap: 'wrap'
          }}>
            <div style={{ flex: '1 1 200px', minWidth: '200px', position: 'relative' }}>
              <MagnifyingGlassIcon 
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  height: '18px',
                  width: '18px',
                  color: '#9ca3af'
                }}
              />
              <input
                type="text"
                placeholder={t('events.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 40px',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb',
                  fontSize: '14px',
                  outline: 'none',
                  backgroundColor: 'white',
                  transition: 'all 0.2s',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
              />
            </div>
          </div>

          {/* Category Filter */}
          <div 
            className="category-carousel"
            style={{ 
              width: '100%', 
              overflowX: 'auto',
              paddingBottom: '8px',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              paddingRight: '16px'
            }}
          >
            <div style={{
              display: 'flex',
              gap: '8px',
              padding: '8px 0',
              minWidth: 'max-content',
              width: 'max-content'
            }}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleClearAll}
                style={{
                  padding: '8px 16px',
                  borderRadius: '20px',
                  border: hasActiveFilters ? '2px solid #ea580c' : '1px solid #e5e7eb',
                  backgroundColor: hasActiveFilters ? '#fee2e2' : (selectedCategory === '' ? '#ea580c' : 'white'),
                  color: hasActiveFilters ? '#991b1b' : (selectedCategory === '' ? 'white' : '#374151'),
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                {hasActiveFilters && (
                  <XMarkIcon style={{ height: '14px', width: '14px' }} />
                )}
                {hasActiveFilters ? t('events.clearAll') : t('events.allCategoriesBtn')}
              </motion.button>

              {[
                { value: 'Wellness & Fitness', label: '💪 Wellness & Fitness' },
                { value: 'Music & Nightlife', label: '🎵 Music & Nightlife' },
                { value: 'Food & Social', label: '🍽️ Food & Social' },
                { value: 'Arts & Culture', label: '🎨 Arts & Culture' },
                { value: 'Education & Workshops', label: '🛠️ Education & Workshops' },
                { value: 'Entertainment', label: '🎭 Entertainment' },
                { value: 'Promos', label: '🎁 Promos' }
              ].map((category) => (
                <motion.button
                  key={category.value}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleCategorySelect(category.value)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '20px',
                    border: '1px solid #e5e7eb',
                    backgroundColor: selectedCategory === category.value ? '#f5933d' : 'white',
                    color: selectedCategory === category.value ? 'white' : '#374151',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.2s',
                    flexShrink: 0
                  }}
                >
                  {category.label}
                </motion.button>
              ))}
            </div>
          </div>

          {/* Subtype Carousel */}
          {((selectedCategory && EVENT_SUBTYPES[selectedCategory]) || selectedSubtype) && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              style={{
                marginTop: '0px',
                width: '100%',
                overflowX: 'auto',
                paddingBottom: '8px',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                paddingLeft: '0',
                paddingRight: '16px'
              }}
              className="subtype-carousel"
            >
              <div 
                style={{
                  display: 'flex',
                  gap: '8px',
                  minWidth: 'max-content',
                  padding: '4px 0',
                  width: 'max-content'
                }}
              >
                {(() => {
                  const categoryToShow = selectedCategory || 
                    Object.keys(EVENT_SUBTYPES).find(cat => 
                      EVENT_SUBTYPES[cat].some(sub => sub.value === selectedSubtype)
                    );
                  
                  return EVENT_SUBTYPES[categoryToShow]?.map((subtype) => (
                    <motion.button
                      key={subtype.value}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleSubtypeSelect(subtype.value)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '16px',
                        border: '1px solid #e5e7eb',
                        backgroundColor: selectedSubtype === subtype.value ? '#f5933d' : 'white',
                        color: selectedSubtype === subtype.value ? 'white' : '#374151',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        flexShrink: 0
                      }}
                    >
                      <span>{subtype.icon}</span>
                      {subtype.label}
                    </motion.button>
                  ));
                })()}
              </div>
            </motion.div>
          )}
        </div>

        {/* Scrollable Content */}
        <div style={{ padding: '0 16px 16px 16px' }}>
          {isLoading ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '80px 20px',
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
            }}>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                style={{
                  width: '48px',
                  height: '48px',
                  border: '4px solid #f3f4f6',
                  borderTop: '4px solid #ea580c',
                  borderRadius: '50%',
                  marginBottom: '16px'
                }}
              />
              <p style={{ fontSize: '16px', color: '#6b7280', fontWeight: '500' }}>
                <T k="events.loadingEventsMsg" />
              </p>
            </div>
          ) : events.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '80px 20px',
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
            }}>
              <div style={{ fontSize: '64px', marginBottom: '20px', opacity: 0.5 }}>
                {isError ? '⚠️' : '🔍'}
              </div>
              <h3 style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#374151',
                marginBottom: '12px'
              }}>
                {isError ? <T k="events.connectionFailed" /> : <T k="events.noEvents" />}
              </h3>
              <p style={{
                fontSize: '16px',
                color: '#6b7280',
                marginBottom: '24px'
              }}>
                {isError ? <T k="events.unableToConnect" /> : <T k="events.tryAdjustFilters" />}
              </p>
              <button
                onClick={handleClearAll}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#ea580c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                <T k="events.clearFilters" />
              </button>
            </div>
          ) : (
            <>
              {/* Events Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '16px',
                marginBottom: '20px'
              }}>
                {events.map((event) => (
                  <AllEventCard
                    key={event.id}
                    event={event}
                    onLike={handleLikeEvent}
                    isLiked={likedEvents.has(event.id)}
                    onClick={() => handleEventClick(event)}
                    t={t}
                  />
                ))}
              </div>

              {/* Intersection Observer Target */}
              <div ref={observerTarget} style={{ height: '20px' }} />

              {/* Loading More Indicator */}
              {isFetchingNextPage && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '20px'
                }}>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    style={{
                      width: '32px',
                      height: '32px',
                      border: '3px solid #f3f4f6',
                      borderTop: '3px solid #ea580c',
                      borderRadius: '50%'
                    }}
                  />
                </div>
              )}

              {/* End of Results */}
              {!hasNextPage && events.length > 0 && (
                <div style={{
                  textAlign: 'center',
                  padding: '20px',
                  color: '#6b7280',
                  fontSize: '14px'
                }}>
                  {t('events.endOfList')}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AllEventsPage;
